import React, {
    useState,
    forwardRef,
    useImperativeHandle,
    useCallback,
    useRef,
} from 'react';
import { useToast } from '@shared/ui';
import i18n from '@shared/i18n';
import { TRACK_EVENT, logEvent } from 'app/libs/track';
import { useGiveawaySheetModal } from './useGiveawaySheetModal';
import { usePromoProductDateInfo } from './usePromoProductDateInfo';
import { MobileBottomSheet } from './MobileBottomSheet';
import { LaptopModal } from './LaptopModal';

export type GiveawayCartItem = {
    cartId?: string;
    product: Product;
    quantity: number;
    name: string;
    image: ProductDisplayMedia;
    price: Price | undefined;
    giveaways?: CartItem['giveaways'];
};

export interface GiveawaySheetModalReturnRef {
    showGiveaway: (cartItem: GiveawayCartItem) => void;
}

interface GiveawaySheetModalPropTypes {
    calculate?: <T = unknown>(action: () => Promise<T | null>) => Promise<T | null>;
    isMobile: boolean;
}

export const GiveawaySheetModal = forwardRef<
    GiveawaySheetModalReturnRef,
    GiveawaySheetModalPropTypes & { isMobile: boolean }
>((props, ref) => {
    const { isMobile, calculate } = props;
    const toast = useToast();
    const [visible, setVisible] = useState(false);
    const [isEdit, setIsEdit] = useState(false);
    const [parentProductInfo, setParentProductInfo] = useState<{
        name: string;
        image: ProductDisplayMedia;
        price: Price | undefined;
    } | null>(null);
    const [selectedQuantity, setSelectedQuantity] = useState<Record<string, number>>({});
    const cartIdRef = useRef<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const {
        state: {
            parentProduct,
            isLoading,
            promoProductDateInfo,
            promoProductDetailMap,
            promoProductPayloadMap,
            selectedGiveawayProductMap,
            selectedGiveawayProductSpecMap,
        },
        actions: {
            addToCart,
            updateParentProduct,
            updateParentProductCount,
            updateParentProductSpec,
            updateSelectedGiveawayProduct,
            updatePromoProduct,
            removeAllGiveawayProduct,
            updateSelectedGiveawayProductSpec,
            refetchGiveawayProducts,
            clearAllSelections,
        },
    } = useGiveawaySheetModal({
        visible,
    });

    const { countdown, dateInfoText } = usePromoProductDateInfo(promoProductDateInfo);

    const [productDetail, setProductDetail] = useState<Product | null>(null);

    const initializeGiveaways = useCallback(
        (giveaways: CartItem['giveaways']) => {
            const productMap = giveaways.reduce(
                (acc, giveaway) => {
                    if (!acc[giveaway.product_id]) {
                        acc[giveaway.product_id] = { quantity: 0, specs: [] };
                    }
                    acc[giveaway.product_id].quantity += giveaway.quantity;

                    if (giveaway.spec_detail_id) {
                        for (let i = 0; i < giveaway.quantity; i++) {
                            acc[giveaway.product_id].specs.push(giveaway.spec_detail_id);
                        }
                    }

                    return acc;
                },
                {} as Record<string, { quantity: number; specs: string[] }>
            );

            const initialQuants: Record<string, number> = {};

            Object.entries(productMap).forEach(([productId, { quantity, specs }]) => {
                updatePromoProduct(productId, quantity);
                updateSelectedGiveawayProduct(productId);
                specs.forEach((specDetailId, index) => {
                    updateSelectedGiveawayProductSpec(productId, index, specDetailId);
                });
                initialQuants[productId] = quantity;
            });

            setSelectedQuantity(initialQuants);
        },
        [
            updatePromoProduct,
            updateSelectedGiveawayProduct,
            updateSelectedGiveawayProductSpec,
        ]
    );

    useImperativeHandle(ref, () => ({
        showGiveaway: (payload) => {
            setProductDetail(payload.product);

            const parentProductPayload =
                payload.cartId && 'productId' in payload.product
                    ? {
                          product_id: payload.product.productId,
                          quantity: payload.quantity,
                      }
                    : {
                          product_id: payload.product.id,
                          quantity: payload.quantity,
                      };

            updateParentProduct(parentProductPayload as BuyerAddToCartPayload);

            setParentProductInfo({
                name: payload.name,
                image: payload.image,
                price: payload.price,
            });

            if (payload.cartId && payload.giveaways) {
                initializeGiveaways(payload.giveaways);
                setIsEdit(true);
                cartIdRef.current = payload.cartId;
            }

            setVisible(true);
        },
    }));

    const isAllSpecsSelected = useCallback(() => {
        return Object.entries(selectedGiveawayProductMap).every(
            ([productId, isSelected]) => {
                if (!isSelected) return true;
                const product = promoProductDetailMap[productId];

                if (product?.spec_details.length === 0) return true;

                const selectedSpecs = selectedGiveawayProductSpecMap[productId];

                return (
                    selectedSpecs &&
                    selectedSpecs.length === promoProductPayloadMap[productId].quantity &&
                    selectedSpecs.every((spec) => spec !== undefined)
                );
            }
        );
    }, [
        selectedGiveawayProductMap,
        promoProductDetailMap,
        selectedGiveawayProductSpecMap,
        promoProductPayloadMap,
    ]);

    const handleClose = () => {
        clearAllSelections();
        setVisible(false);
    };

    const handleSubmit = async () => {
        try {
            setIsSubmitting(true);
            const resp = calculate
                ? await calculate(async () => addToCart(cartIdRef.current))
                : await addToCart(cartIdRef.current);

            if (resp) {
                handleClose();

                logEvent(TRACK_EVENT.ADD_TO_CART, {
                    event: TRACK_EVENT.ADD_TO_CART,
                });
            }
        } catch (error) {
            console.error(error);
            clearAllSelections();
            refetchGiveawayProducts();
            toast({
                title: i18n.gettext('商品庫存不足或其他錯誤，請再試一次！'),
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    const Component = isMobile ? MobileBottomSheet : LaptopModal;

    return (
        <Component
            visible={visible}
            handleClose={handleClose}
            handleSubmit={handleSubmit}
            isLoading={isLoading}
            isSubmitting={isSubmitting}
            parentProduct={parentProduct}
            parentProductDetail={productDetail}
            parentProductInfo={parentProductInfo}
            promoProductDateInfo={promoProductDateInfo}
            countdown={countdown}
            dateInfoText={dateInfoText}
            promoProductDetailMap={promoProductDetailMap}
            promoProductPayloadMap={promoProductPayloadMap}
            selectedGiveawayProductMap={selectedGiveawayProductMap}
            selectedGiveawayProductSpecMap={selectedGiveawayProductSpecMap}
            updateParentProductCount={updateParentProductCount}
            updateParentProductSpec={updateParentProductSpec}
            removeAllGiveawayProduct={removeAllGiveawayProduct}
            updateSelectedGiveawayProduct={updateSelectedGiveawayProduct}
            updatePromoProduct={updatePromoProduct}
            updateSelectedGiveawayProductSpec={updateSelectedGiveawayProductSpec}
            selectedQuantity={selectedQuantity}
            isAllSpecsSelected={isAllSpecsSelected}
            isEdit={isEdit}
        />
    );
});

GiveawaySheetModal.displayName = 'GiveawaySheetModal';
